import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Snackbar, Alert,
} from '@mui/material';
import AppShell from '../components/Layout/AppShell';
import GridProcessos from '../components/Processos/GridProcessos';
import MenuLateralProcesso from '../components/Processos/MenuLateralProcesso';
import BarraAcoesProcesso from '../components/Processos/BarraAcoesProcesso';
import AbaAcompanhamento from '../components/Processos/abas/AbaAcompanhamento';
import AbaHistorico from '../components/Processos/abas/AbaHistorico';
import AbaRecebimento from '../components/Processos/abas/AbaRecebimento';
import AbaFinanceira from '../components/Processos/abas/AbaFinanceira';
import AbaDocumentos from '../components/Processos/abas/AbaDocumentos';
import AbaEmail from '../components/Processos/abas/AbaEmail';
import AbaDevedor from '../components/Processos/abas/AbaDevedor';
import AbaCliente from '../components/Processos/abas/AbaCliente';
import AbaRecalculos from '../components/Processos/abas/AbaRecalculos';
import AbaJuridico from '../components/Processos/abas/AbaJuridico';
import AbaFases from '../components/Processos/abas/AbaFases';
import { CardWrapper, ButtonUI } from '../components/UI';
import { rota } from '../prototipo.config';
import * as C from '../theme/colors.constants';
import {
  findStatusProcessosAction,
  findRequisicoesAction,
  findTipoTituloAction,
  findStatusTituloAction,
  findMotivoBaixaTituloAction,
  findMotivosExclusaoTituloAction,
  findTiposHistoricoProcesso,
  findTiposAgendamentosAction,
  findTiposTitulosAgendamentosAction,
} from '../../store/Global/LoadSelectors/loadSelectors.saga';
import { buscarTodosTituloCompromisso } from '../../services/core/tituloCompromisso/TituloCompromisso.services';
import {
  buscarAcompanhamentoDoProcessoService,
  findInformacoesAdicionaisInpescaoProcessoService,
  buscarHistoricoProcesso,
  updateTipoExibicaoHistorico,
} from '../../services/core/processos/processosService';
import { tipoExibicaoHistorico } from '../../models/types';
import { ORDENACAO_HISTORICOS_PROCESSO } from '../components/Processos/Historico/historicoController';
import { RESULT_NOT_FOUND } from '../../helpers/constants/global.constants';
import useBuscarProcessoPorNumero from '../hooks/useBuscarProcessoPorNumero';
import usePaginacaoOrdenacaoPorProcesso from '../hooks/usePaginacaoOrdenacaoPorProcesso';
import { getDadosResumo } from '../../services/core/processos/processosAcordosService';
import { buscarAntecedentesDoProcessoService } from '../../services/core/processos/processoAntecedenteService';
import { inspecionarTitulosProcessoService } from '../../services/core/processos/processosTitulosService';
import {
  buscarIdFaseAcordoSeguro, buscarIdFaseAcordoSeguroExterno, buscarIdFaseBaixa,
} from '../../services/core/fase/fase.services';
import { mapearAntecedentes } from '../components/Processos/Antecedentes/antecedentesController';
import { mapearTitulos } from '../components/Processos/Titulos/titulosController';
import TableUtils from '../../helpers/utils/table.utils';
import { tiposConsultasAntecedentesTypes } from '../../types/tiposConsultasAntecedentesTypes';
import { mapearResultadoParaGrid } from './controleProcessosPage.mapper';

/**
 * Tamanho de página alto para aproximar "buscar todos" — `buscarTodosTituloCompromisso`
 * é paginado no servidor (mesmo padrão de `TAMANHO_PAGINA_ATALHO`, ver
 * `pesquisaProcessosUnificada.saga.js`); não validado contra volume real.
 */
const TAMANHO_PAGINA_TITULOS_COMPROMISSO = 500;

const TAMANHO_PAGINA_ANTECEDENTES_PADRAO = 10;
const ORDENACAO_ANTECEDENTES_PADRAO = { campo: 'idProcesso', direcao: 'ASC' };

const TAMANHO_PAGINA_TITULOS_PADRAO = 10;
const ORDENACAO_TITULOS_PADRAO = { campo: 'dataVencimento', direcao: 'ASC' };

/**
 * Tamanho de página alto para o cálculo de `todosTitulosBaixados` (Etapa 7,
 * gate do "Demonstrativo de Valores") — busca todos os títulos do processo
 * de uma vez, mesmo padrão já usado acima para `TAMANHO_PAGINA_TITULOS_COMPROMISSO`.
 * Simplificação deliberada da tela real (`Titulos.jsx`): a tela real só faz
 * essa 2ª chamada CONDICIONALMENTE, quando a página atual (já carregada para
 * a grid) não contém todos os títulos — aqui sempre busca todos numa
 * chamada dedicada e independente da paginação da grid, evitando a cadeia
 * condicional (1º fetch da grid → checar se cobriu tudo → 2º fetch só se
 * necessário). Custo aceito: 1 requisição a mais por troca de processo,
 * sempre, mesmo quando a grid já cobriria o total numa única página.
 */
const TAMANHO_PAGINA_TODOS_TITULOS = 9999;

/**
 * Controle de Processos.
 *
 * Estrutura em três faixas horizontais, como nos prints:
 *   [ grid redimensionável ] [ painel de detalhe (flex) ] [ menu lateral direito ]
 *
 * A grid tem largura própria (estado interno, arrastável). O painel do meio é
 * `flex: 1` com `minWidth: 0` — esse `minWidth: 0` é o que permite ele encolher
 * quando a grid cresce; sem isso o flexbox usa o conteúdo como largura mínima e
 * o layout estoura para a direita.
 *
 * O menu lateral tem largura própria em `clamp()` e é `position: sticky`, então
 * acompanha o scroll das abas mais longas (Devedor, Cliente, Fases).
 *
 * Dado real: `resultado` vem de `state.pesquisaProcessosUnificada` (populado por
 * `PesquisaProcessosPage.jsx`, que navega para cá ao concluir uma pesquisa com
 * sucesso — ver `src/prototipo/CLAUDE.md`). `resultado === null` = nenhuma
 * pesquisa nesta sessão (rota acessada direto pela URL); `resultado === []` =
 * pesquisa concluída sem processos encontrados.
 */
export default function ControleProcessosPage() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const resultado = useSelector(state => state.pesquisaProcessosUnificada.resultado);
  const processos = useMemo(() => mapearResultadoParaGrid(resultado), [resultado]);
  const [processo, setProcesso] = useState(null);
  const [aba, setAba] = useState('acompanhamento');

  // "Latest ref" — mutado direto no corpo (não em efeito), mesmo padrão de
  // BarraAcoesProcesso.jsx/aoSucesso: guarda contra qualquer resposta
  // assíncrona tardia (de um handler disparado para o processo ANTERIOR)
  // aplicar seus efeitos colaterais sobre a tela do processo novo. Declarado
  // cedo (usado por vários fetches/handlers ao longo deste componente).
  const idProcessoRef = useRef(processo?.id);
  idProcessoRef.current = processo?.id;

  // Entrada via "Inspecionar" da grid de Antecedentes (ajuste pós-Etapa 5) —
  // uma nova aba do protótipo aberta com `?nroProcesso=X` reproduz o mesmo
  // mecanismo de busca por número de processo já usado em `HomePage.jsx`/
  // `PesquisaProcessosPage.jsx` (`useBuscarProcessoPorNumero`, extraído
  // nesta sessão — 3ª página com o mesmo fluxo, limite documentado em
  // `src/prototipo/CLAUDE.md`), disparado no mount desta aba em vez de por
  // um campo de busca. Efeito mount-only (`[]`) de propósito — esta aba só
  // precisa reproduzir esse resultado uma vez; não deve reagir a nenhuma
  // mudança posterior de `location`/`resultado` causada por interações
  // normais do usuário na mesma aba depois disso.
  const { buscar: buscarPorNroProcesso } = useBuscarProcessoPorNumero();
  // 2 estados separados de propósito (achado testando ao vivo no browser):
  // `erroBuscaPorNroProcesso` é permanente (nunca reseta sozinho) e decide o
  // texto do card "Nenhuma pesquisa..."/"Não encontrado"; `snackbarErroAberto`
  // só controla a visibilidade TEMPORÁRIA do Snackbar (`autoHideDuration`).
  // Usar o MESMO estado para os dois já foi tentado e é um bug real: o
  // auto-hide do Snackbar (`onClose`, 4s) resetava o estado compartilhado de
  // volta para `false`, revertendo o texto do card para a mensagem genérica
  // assim que o Snackbar sumia — só visível testando ao vivo, não no código.
  const [erroBuscaPorNroProcesso, setErroBuscaPorNroProcesso] = useState(false);
  const [snackbarErroAberto, setSnackbarErroAberto] = useState(false);

  useEffect(() => {
    const nroProcesso = new URLSearchParams(location.search).get('nroProcesso');
    if (!nroProcesso) {
      return;
    }

    buscarPorNroProcesso(nroProcesso, {
      onNaoEncontrado: () => {
        setErroBuscaPorNroProcesso(true);
        setSnackbarErroAberto(true);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mesmo endpoint/selector já usado pelo campo "Status" do formulário de
  // PesquisaProcessosPage.jsx (GET {CORE}/processos/lista-status, via
  // findStatusProcessosAction -> state.selectors.statusProcessos.selector).
  // Disparado aqui também (não só lá) para não depender da ordem de
  // navegação entre as duas páginas — takeLatest torna o 2º dispatch
  // barato quando o selector já está carregado.
  const statusSelector = useSelector(state => state.selectors.statusProcessos.selector);
  const statusOptions = useMemo(() => statusSelector.map(s => s.value), [statusSelector]);

  useEffect(() => {
    dispatch(findStatusProcessosAction());
    dispatch(findRequisicoesAction());
    dispatch(findTipoTituloAction());
    dispatch(findStatusTituloAction());
    dispatch(findMotivoBaixaTituloAction());
    dispatch(findMotivosExclusaoTituloAction());
    dispatch(findTiposHistoricoProcesso());
    dispatch(findTiposAgendamentosAction());
    dispatch(findTiposTitulosAgendamentosAction());
  }, [dispatch]);

  // Selectors dos dialogs "Editar Título"/"Excluir Título" (AW2-1034 Etapas
  // 4/5) — mesmo padrão de `statusOptions` acima: dispatch no mount,
  // `useSelector` para ler. `motivosExclusaoTitulo` dispatchado aqui junto
  // com os demais (eager, no mount) por consistência com este arquivo —
  // diverge da tela real (`Titulos.jsx`, que só dispatcha quando o dialog de
  // exclusão abre), decisão deliberada de simplicidade já que carregar a
  // lista mais cedo não tem custo funcional.
  const tipoTituloOptions = useSelector(state => state.selectors.tipoTitulo.selector);
  const statusTituloOptions = useSelector(state => state.selectors.statusTitulo.selector);
  const motivosBaixaOptions = useSelector(state => state.selectors.motivosBaixaTitulo.selector);
  const motivosExclusaoOptions = useSelector(state => state.selectors.motivosExclusaoTitulo.selector);

  // Mesmo padrão de statusOptions acima — selector real já usado pela tela
  // de Inspecionar Processo (AW2-912), lido aqui para alimentar o painel de
  // Gerar/Atender Requisição da barra de ações.
  const requisicoesSelector = useSelector(state => state.selectors.requisicoes.selector);

  // Alimenta o select "Tipo do Histórico" do Bloco de Anotação (Etapa 5) —
  // mesmo selector/filtro (id 7/9/10) que a tela real usa em `AcaoAnotar.jsx`.
  const tiposHistoricoOptions = useSelector(state => state.selectors.tiposHistoricoProcesso.selector);

  // Alimentam os selects "Tipo"/"Título" do dialog "Agendar Compromisso"
  // (Etapa 9) — referência global, não por-fase (diferente de `pastasDaFase`/
  // `motivosBaixa`, dispatchados sob demanda dentro de `MudancaFaseEnvio.jsx`),
  // por isso seguem o mesmo padrão eager-load do container usado acima para
  // `tiposHistoricoOptions`.
  const tiposAgendamentosOptions = useSelector(state => state.selectors.tiposAgendamentos.selector);
  const tiposTitulosAgendamentosOptions = useSelector(
    state => state.selectors.tiposTitulosAgendamentos.selector,
  );

  // `buscarTodosTituloCompromisso` (mesmo service já usado pelo CRUD de
  // Títulos de Compromisso em produção, src/pages/TituloCompromisso/) não
  // tem wrapper em loadSelectors.saga.js — chamado direto, mesmo padrão já
  // usado por `buscarInformacoesDashboardService` em PesquisaProcessosPage.jsx
  // (sem tratamento de erro: falha de rede deixa o filtro sem opções, sem
  // quebrar a tela). `condicao` de cada processo continua um placeholder
  // fixo (`null`, ver controleProcessosPage.mapper.js) — as opções aqui
  // vêm de dado real, mas o filtro em si não encontra nenhum processo até
  // `condicao` também ser conectada a um campo real do ProcessoDTO (decisão
  // do usuário: implementar as opções agora, antecipando essa conexão).
  const [condicaoOptions, setCondicaoOptions] = useState([]);

  useEffect(() => {
    buscarTodosTituloCompromisso(0, TAMANHO_PAGINA_TITULOS_COMPROMISSO)
      .then((response) => {
        setCondicaoOptions((response?.data?.content || []).map(titulo => titulo.descricao));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setProcesso(processos[0] ?? null);
  }, [processos]);

  // Dado real da barra de sinalizadores (AW2-911) — mesmo endpoint já usado por
  // InspecionarProcessoPage.jsx (GET {CORE}/processos/{id}/inspecao). Chamado
  // direto do container, sem saga, mesmo padrão já usado acima para
  // condicaoOptions: sem wrapper em loadSelectors.saga.js e sem toast de erro
  // em falha de rede (a barra fica sem sinalizador, a tela não quebra).
  const [sinalizacaoInfo, setSinalizacaoInfo] = useState(null);

  useEffect(() => {
    setSinalizacaoInfo(null);

    if (!processo?.id) {
      return undefined;
    }

    let ignore = false;
    findInformacoesAdicionaisInpescaoProcessoService(processo.id)
      .then(({ data }) => {
        if (!ignore) {
          setSinalizacaoInfo(data);
        }
      })
      .catch(() => {});

    return () => {
      ignore = true;
    };
  }, [processo?.id]);

  // Dado real do painel "Cálculo do Processo" (AW2-1032 Etapa 1) — mesmo
  // endpoint já usado por Acompanhamento.jsx real (GET
  // {CORE}/processos/acompanhamento-processo). Mesmo padrão de
  // sinalizacaoInfo acima: reset síncrono + guard de cancelamento, sem toast
  // em falha de rede (o card fica com "—", a tela não quebra).
  const [dadosProcesso, setDadosProcesso] = useState(null);

  useEffect(() => {
    setDadosProcesso(null);

    if (!processo?.id) {
      return undefined;
    }

    let ignore = false;
    buscarAcompanhamentoDoProcessoService(processo.id)
      .then(({ data }) => {
        if (!ignore) {
          setDadosProcesso(data);
        }
      })
      .catch(() => {});

    return () => {
      ignore = true;
    };
  }, [processo?.id]);

  // Dado real do card "Títulos do processo" (AW2-1034 Etapa 1) — mesmo
  // endpoint já usado por Titulos.jsx real (GET {CORE}/processos/{id}/titulos),
  // agora com paginação/ordenação reais (Etapa 2), via o hook compartilhado
  // `usePaginacaoOrdenacaoPorProcesso` (extraído na Etapa 2 de AW2-1054 —
  // esta é a 2ª aplicação, ver `Historico/CLAUDE.md`/`prototipo/CLAUDE.md`
  // para a 1ª em Antecedentes e a razão de Histórico ter ficado de fora).
  const titulosHook = usePaginacaoOrdenacaoPorProcesso(
    processo?.id,
    (idProcessoAlvo, pagina, tamanho, ordenacao) => inspecionarTitulosProcessoService(
      idProcessoAlvo, pagina, tamanho, [`${ordenacao.campo},${ordenacao.direcao}`],
    ).then(({ data }) => ({ content: mapearTitulos(data.content), totalElements: data.totalElements })),
    { tamanhoPaginaPadrao: TAMANHO_PAGINA_TITULOS_PADRAO, ordenacaoPadrao: ORDENACAO_TITULOS_PADRAO },
  );
  const {
    dados: titulos,
    total: titulosTotal,
    pagina: titulosPagina,
    tamanhoPagina: titulosTamanhoPagina,
    ordenacao: titulosOrdenacao,
    onChangeOrdenacao: onChangeOrdenacaoTitulos,
    onChangePagina: onChangePaginaTitulos,
    onChangeTamanhoPagina: onChangeTamanhoPaginaTitulos,
    buscarComParametros: buscarTitulosComParametros,
  } = titulosHook;

  // Toggle "Expandir/Recolher Títulos" (Etapa 3) — só troca qual conjunto de
  // colunas `TableTitulosProcesso` exibe, sem refetch (o dado paginado já
  // carrega todos os campos de ambos os modos, ver `titulosController.js`).
  const [modoGridTitulos, setModoGridTitulos] = useState('PADRAO');
  useEffect(() => {
    setModoGridTitulos('PADRAO');
  }, [processo?.id]);

  const onAlternarModoGridTitulos = () => {
    setModoGridTitulos(atual => (atual === 'PADRAO' ? 'DETALHADA' : 'PADRAO'));
  };

  // Gate "Demonstrativo de Valores" (Etapa 7) — `todosTitulosBaixados` só é
  // `true` quando TODOS os títulos do processo (não só a página atual da
  // grid) têm `idMotivoBaixa` preenchido. Fetch independente do estado
  // paginado da grid (`titulos`/`titulosTotal` acima) — ver nota de
  // `TAMANHO_PAGINA_TODOS_TITULOS` sobre a simplificação em relação à tela
  // real. `false` (fail-closed) enquanto carrega ou em caso de erro — nunca
  // deixa o botão habilitado por engano antes da resposta chegar.
  const [todosTitulosBaixados, setTodosTitulosBaixados] = useState(false);

  const buscarTodosTitulosBaixados = useCallback((idProcessoAlvo) => {
    inspecionarTitulosProcessoService(
      idProcessoAlvo,
      0,
      TAMANHO_PAGINA_TODOS_TITULOS,
      [`${ORDENACAO_TITULOS_PADRAO.campo},${ORDENACAO_TITULOS_PADRAO.direcao}`],
    )
      .then(({ data }) => {
        if (idProcessoRef.current === idProcessoAlvo) {
          const todosTitulos = data.content || [];
          setTodosTitulosBaixados(todosTitulos.length > 0 && todosTitulos.every(t => t.idMotivoBaixa != null));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setTodosTitulosBaixados(false);

    if (!processo?.id) {
      return;
    }

    buscarTodosTitulosBaixados(processo.id);
  }, [processo?.id, buscarTodosTitulosBaixados]);

  // Dado real de habilitação do botão ACORDO (AW2-1032 Etapa 3) — mesmo
  // endpoint já usado por Acordos.jsx real (GET
  // {CORE}/processos/acordos/{id}/informacoes). Mesmo padrão de
  // sinalizacaoInfo/dadosProcesso acima: reset síncrono + guard de
  // cancelamento, sem toast em falha de rede (o botão fica desabilitado por
  // padrão — fail-closed — a tela não quebra).
  const [infoAdicionaisAcordo, setInfoAdicionaisAcordo] = useState(null);

  useEffect(() => {
    setInfoAdicionaisAcordo(null);

    if (!processo?.id) {
      return undefined;
    }

    let ignore = false;
    getDadosResumo(processo.id)
      .then(({ data }) => {
        if (!ignore) {
          setInfoAdicionaisAcordo(data);
        }
      })
      .catch(() => {});

    return () => {
      ignore = true;
    };
  }, [processo?.id]);

  // Dado real do card "Processos Antecedentes" (AW2-1033 Etapa 1/2) — mesmo
  // endpoint já usado por AntecedentesProcessos.jsx real (GET
  // {CORE}/processos/{id}/antecedentes), agora com paginação/ordenação reais
  // (Etapa 2), via o hook compartilhado `usePaginacaoOrdenacaoPorProcesso`
  // (extraído na Etapa 2 de AW2-1054 — 1ª aplicação; a 2ª é `titulosHook`
  // acima). O filtro `tiposConsultasAntecedentes` é passado como `extra` —
  // não pode vir de closure porque `onAlternarTipoConsultaAntecedentes`
  // troca o filtro E refaz o fetch no mesmo clique, antes do próximo render.
  const antecedentesHook = usePaginacaoOrdenacaoPorProcesso(
    processo?.id,
    (idProcessoAlvo, pagina, tamanho, ordenacao, tipoConsulta) => buscarAntecedentesDoProcessoService({
      idProcesso: idProcessoAlvo,
      pageable: { page: pagina, size: tamanho },
      ordenacao: [`${ordenacao.campo},${ordenacao.direcao}`],
      tiposConsultasAntecedentes: tipoConsulta,
    })
      .then(({ data }) => ({ content: mapearAntecedentes(data.content), totalElements: data.totalElements }))
      .catch((erro) => {
        // Falha de rede não pode deixar o card preso em "Carregando..." para
        // sempre — o `catch` genérico do hook resolve para vazio; aqui só
        // logamos no console (não visível ao usuário) para não confundir
        // "sem antecedentes" com falha de rede real ao diagnosticar em
        // produção, e relançamos para o hook aplicar o fallback padrão.
        console.error('Falha ao buscar antecedentes do processo', erro);
        throw erro;
      }),
    {
      tamanhoPaginaPadrao: TAMANHO_PAGINA_ANTECEDENTES_PADRAO,
      ordenacaoPadrao: ORDENACAO_ANTECEDENTES_PADRAO,
      extraPadrao: tiposConsultasAntecedentesTypes.simples,
    },
  );
  const {
    dados: antecedentes,
    total: antecedentesTotal,
    pagina: antecedentesPagina,
    tamanhoPagina: antecedentesTamanhoPagina,
    ordenacao: antecedentesOrdenacao,
    onChangeOrdenacao: onChangeOrdenacaoAntecedentesSemTipo,
    onChangePagina: onChangePaginaAntecedentesSemTipo,
    onChangeTamanhoPagina: onChangeTamanhoPaginaAntecedentesSemTipo,
    setPagina: setAntecedentesPagina,
    setOrdenacao: setAntecedentesOrdenacao,
    setTamanhoPagina: setAntecedentesTamanhoPagina,
    buscarComParametros: buscarAntecedentesComParametros,
  } = antecedentesHook;

  // Seleção (Etapa 3) — re-seedada a cada novo fetch da lista (troca de
  // processo, página, ordenação); mais simples que preservar seleção entre
  // páginas (aceitável: a mutação real ainda não está wireada nesta etapa).
  const [antecedentesSelecionados, setAntecedentesSelecionados] = useState(new Map());
  useEffect(() => {
    setAntecedentesSelecionados(TableUtils.makeAllIdsSelections((antecedentes || []).map(a => a.id)));
  }, [antecedentes]);

  // Filtro por tipo de ação (Unificar/Juntar) — mesmo parâmetro que a tela
  // real usa para mostrar só os antecedentes elegíveis para a ação escolhida
  // (`tiposConsultasAntecedentes=UNIFICAR|JUNTAR`, `SIMPLES` = nenhuma ação
  // escolhida ainda, lista completa).
  const [tipoConsultaAntecedentes, setTipoConsultaAntecedentes] = useState(
    tiposConsultasAntecedentesTypes.simples,
  );
  useEffect(() => {
    setTipoConsultaAntecedentes(tiposConsultasAntecedentesTypes.simples);
  }, [processo?.id]);

  // `tipoConsultaAntecedentes` injetado como `extra` a cada chamada — os 3
  // handlers do hook não têm acesso a esse state (ele vive no container).
  const onChangeOrdenacaoAntecedentes = campo => onChangeOrdenacaoAntecedentesSemTipo(campo, tipoConsultaAntecedentes);
  const onChangePaginaAntecedentes = novaPagina => onChangePaginaAntecedentesSemTipo(novaPagina, tipoConsultaAntecedentes);
  const onChangeTamanhoPaginaAntecedentes = event => onChangeTamanhoPaginaAntecedentesSemTipo(event, tipoConsultaAntecedentes);

  // Clicar em Unificar/Juntar refaz o fetch da grid filtrado por tipo de
  // ação — mesmo comportamento de `handleOpenResumoProcessoByType`/
  // `AntecedentesProcessosController.buscarAntecedentesEIdsAntecedentes` da
  // tela real: só os antecedentes elegíveis para aquela ação aparecem.
  // Clicar no mesmo tipo já ativo volta para `SIMPLES` (mesma semântica de
  // `actionButtonType.changeType`) — sempre refaz o fetch, mesmo ao
  // "desligar". Página/ordenação resetadas para os defaults (mesmo padrão
  // real, que não preserva paginação/ordenação ao trocar de tipo); tamanho
  // de página mantido (pequena melhoria de UX sobre o real, sem risco — só
  // um parâmetro de paginação diferente no mesmo endpoint).
  const onAlternarTipoConsultaAntecedentes = (tipo) => {
    const novoTipo = tipoConsultaAntecedentes === tipo ? tiposConsultasAntecedentesTypes.simples : tipo;
    setTipoConsultaAntecedentes(novoTipo);
    setAntecedentesPagina(0);
    setAntecedentesOrdenacao(ORDENACAO_ANTECEDENTES_PADRAO);
    buscarAntecedentesComParametros(processo.id, 0, antecedentesTamanhoPagina, ORDENACAO_ANTECEDENTES_PADRAO, novoTipo);
  };

  // Aba Histórico (AW2-1054 Etapa 1) — checkbox "Limpar campos": preferência
  // de UI controlada pelo usuário, não dado por-processo, por isso NÃO entra
  // no efeito de reset por `processo?.id` (diferente de todo outro estado
  // desta página).
  const [limparCamposHistorico, setLimparCamposHistorico] = useState(false);

  // Etapa 10 — "primeira visita a um processo nesta sessão" se comporta como
  // "marcado" (reseta), mesmo com o checkbox desmarcado — não há nada
  // legítimo para preservar na primeira vez que o usuário abre um processo
  // nunca visitado antes. `Set` num ref (não `useState`) porque só precisa
  // ser LIDO durante o render (nunca dispara re-render sozinho) e escrito
  // depois do commit (`useEffect`) — mutar um ref durante o render, em vez
  // de só ler, seria o anti-padrão que o `useEffect` abaixo evita. Vive no
  // container (não em `BlocoAnotacao`/`MudancaFaseEnvio`) porque troca de
  // aba desmonta e remonta esses componentes — um ref local a eles se
  // perderia a cada troca de aba, quebrando "nesta sessão".
  const processosVisitadosHistoricoRef = useRef(new Set());
  const primeiraVisitaAoProcesso = processo?.id != null
    && !processosVisitadosHistoricoRef.current.has(processo.id);
  useEffect(() => {
    if (processo?.id != null) {
      processosVisitadosHistoricoRef.current.add(processo.id);
    }
  }, [processo?.id]);

  // Flag única repassada aos componentes da aba Histórico que precisam
  // decidir entre resetar ou preservar seu estado ao trocar de processo.
  const deveResetarCamposHistorico = limparCamposHistorico || primeiraVisitaAoProcesso;

  // Lista de Histórico (Etapa 2) — mesmo endpoint real (`buscarHistoricoProcesso`,
  // GET {CORE}/processos/{id}/historicos) e mesma ordenação fixa da tela real
  // (`ORDENACAO_HISTORICOS_PROCESSO` — fixados primeiro, sem ordenação
  // selecionável pelo usuário). Guard `idProcessoRef`+`requestRef` dedicado
  // (mesmo princípio de Títulos/Antecedentes), mas SEM o hook
  // `usePaginacaoOrdenacaoPorProcesso` — scroll-infinito concatena páginas
  // (não substitui) e não tem ordenação/tamanho de página selecionáveis, uma
  // semântica de dado diferente o suficiente para não forçar na mesma
  // abstração (decisão registrada em `src/prototipo/CLAUDE.md`).
  const [historicos, setHistoricos] = useState(null);
  const [carregandoMaisHistoricos, setCarregandoMaisHistoricos] = useState(false);
  const [desabilitarFixarHistorico, setDesabilitarFixarHistorico] = useState(false);
  const [erroFixarHistorico, setErroFixarHistorico] = useState('');

  const historicosIdProcessoRef = useRef(processo?.id);
  historicosIdProcessoRef.current = processo?.id;
  const historicosRequestRef = useRef(0);
  // Guarda síncrona (além do `desabilitarFixarHistorico` assíncrono) contra
  // duplo-clique em "fixar" — um `useState` só reflete o novo valor no
  // próximo render, então 2 cliques disparados antes desse commit ainda
  // leem `desabilitarFixarHistorico === false` no closure do handler e
  // disparariam 2 PUTs concorrentes. Mesmo padrão já usado em dialogs de
  // mutação deste módulo (`DialogEnviarEmailDevedor.jsx`).
  const fixandoHistoricoRef = useRef(false);

  const buscarHistoricos = useCallback((idProcessoAlvo, pagina) => {
    const requestId = historicosRequestRef.current + 1;
    historicosRequestRef.current = requestId;

    const aindaValido = () => historicosIdProcessoRef.current === idProcessoAlvo
      && historicosRequestRef.current === requestId;

    return buscarHistoricoProcesso(idProcessoAlvo, ORDENACAO_HISTORICOS_PROCESSO, { page: pagina })
      .then(({ data }) => {
        if (!aindaValido()) {
          return;
        }
        setHistoricos(anterior => ({
          content: pagina === 0 ? data.content : [...(anterior?.content || []), ...data.content],
          last: data.last,
          numero: data.number,
          idProcesso: idProcessoAlvo,
        }));
      })
      .catch(() => {
        // Falha de rede não pode deixar a lista presa em "Carregando..." para
        // sempre — resolve para vazia, `last: true` evita nova tentativa de
        // scroll infinito contra um endpoint que já falhou.
        if (aindaValido()) {
          setHistoricos({
            content: [], last: true, numero: 0, idProcesso: idProcessoAlvo,
          });
        }
      });
  }, []);

  useEffect(() => {
    setHistoricos(null);
    setCarregandoMaisHistoricos(false);
    setDesabilitarFixarHistorico(false);
    setErroFixarHistorico('');
    fixandoHistoricoRef.current = false;

    if (!processo?.id) {
      return;
    }

    buscarHistoricos(processo.id, 0);
  }, [processo?.id, buscarHistoricos]);

  const onScrollFimHistoricos = useCallback(() => {
    if (!processo?.id || !historicos || historicos.last || carregandoMaisHistoricos) {
      return;
    }
    setCarregandoMaisHistoricos(true);
    buscarHistoricos(processo.id, (historicos.numero ?? 0) + 1)
      .finally(() => {
        if (historicosIdProcessoRef.current === processo.id) {
          setCarregandoMaisHistoricos(false);
        }
      });
  }, [processo?.id, historicos, carregandoMaisHistoricos, buscarHistoricos]);

  // "Fixar"/"desafixar" um item — mesmo endpoint real
  // (`updateTipoExibicaoHistorico`, PUT sem corpo, valor no path) seguido de
  // refetch da página 0 (não é otimista — mesmo comportamento da tela real,
  // `udpateHistoricosWhenChecked`). Note que esse refetch SEMPRE busca a
  // página 0 (substitui, não concatena) — qualquer progresso de scroll-
  // infinito acumulado (páginas 1, 2, ...) é descartado e a lista recua
  // para a 1ª página; paridade aceita com a tela real, não um bug.
  // Guarda dupla contra duplo-clique: `fixandoHistoricoRef` (síncrona,
  // fecha a janela entre 2 cliques antes do próximo render) +
  // `desabilitarFixarHistorico` (assíncrona, reflete no `disabled` da UI).
  // `historicos?.idProcesso !== alvo` cobre a janela entre trocar de
  // processo (que já muda `processo?.id`/`alvo` neste render) e o efeito de
  // reset (que só limpa `historicos` depois do paint) — sem esse guard, um
  // clique nesse intervalo faria PUT com o `idProcessoHistorico` de um item
  // do processo ANTERIOR contra o `idProcesso` NOVO. `historicos` carrega o
  // `idProcesso` de quem o buscou (`buscarHistoricos`), então a comparação é
  // sempre contra o dado que está de fato renderizado, não contra um
  // "deveria já ter resetado" implícito.
  const onFixarHistorico = useCallback((idProcessoHistorico, checked) => {
    const alvo = processo?.id;
    if (!alvo || fixandoHistoricoRef.current || historicos?.idProcesso !== alvo) {
      return;
    }

    fixandoHistoricoRef.current = true;
    const tipoExibicao = checked ? tipoExibicaoHistorico.fixo : tipoExibicaoHistorico.naoFixo;
    setDesabilitarFixarHistorico(true);
    updateTipoExibicaoHistorico(alvo, idProcessoHistorico, tipoExibicao)
      .then(() => buscarHistoricos(alvo, 0))
      .catch(() => {
        if (historicosIdProcessoRef.current === alvo) {
          setErroFixarHistorico('Não foi possível atualizar o histórico.');
        }
      })
      .finally(() => {
        fixandoHistoricoRef.current = false;
        if (historicosIdProcessoRef.current === alvo) {
          setDesabilitarFixarHistorico(false);
        }
      });
  }, [processo?.id, historicos, buscarHistoricos]);

  // Parâmetros de negócio globais (tipos de fase fixos do sistema), não por
  // processo — mesmo padrão de fetch único no mount já usado para
  // `parametrosInternosAbe` (AW2-1032 Etapa 6). Falha de rede é silenciosa:
  // `fasesIds` fica `null`, e a habilitação de Unificar/Juntar (só UX) trata
  // isso como "não pode habilitar" — o backend real segue sendo a validação
  // de verdade.
  const [fasesIds, setFasesIds] = useState(null);

  useEffect(() => {
    Promise.all([
      buscarIdFaseAcordoSeguro(),
      buscarIdFaseBaixa(),
      buscarIdFaseAcordoSeguroExterno(),
    ])
      .then(([respostaAcordoSeguro, respostaBaixa, respostaAcordoSeguroExterno]) => {
        setFasesIds({
          idFaseAcordoSeguro: respostaAcordoSeguro.data,
          idFaseBaixa: respostaBaixa.data,
          idFaseAcordoSeguroExterno: respostaAcordoSeguroExterno.data,
        });
      })
      .catch(() => {});
  }, []);

  const onSelecionarAntecedente = (id) => {
    setAntecedentesSelecionados((atual) => {
      const proximo = new Map(atual);
      proximo.set(id, !atual.get(id));
      return proximo;
    });
  };

  const onSelecionarTodosAntecedentes = (checked) => {
    // Fail-closed enquanto `fasesIds` ainda não carregou (achado em code
    // review): sem `idFaseBaixa` para comparar, nenhuma linha é selecionável
    // — nunca assumir "nenhuma é fase baixa" só porque o dado ainda não
    // chegou. A UI (`TableAntecedentesProcesso.jsx`) já reflete isso
    // desabilitando o checkbox do cabeçalho nesse estado.
    if (fasesIds?.idFaseBaixa == null) {
      return;
    }
    const idFaseBaixaNum = Number(fasesIds.idFaseBaixa);
    setAntecedentesSelecionados((atual) => {
      const proximo = new Map(atual);
      (antecedentes || []).forEach((a) => {
        if (Number(a.idFase) !== idFaseBaixaNum) {
          proximo.set(a.id, checked);
        }
      });
      return proximo;
    });
  };

  // Grid "Antecedentes Ativos" da aba Histórico (Etapa 4) — mirror
  // somente-leitura do MESMO `antecedentes` já buscado acima para
  // Acompanhamento, sem fetch novo. Diferente do padrão de fase Baixa já
  // usado ali (desabilita a linha), aqui a AC pede ocultar completamente —
  // `antecedentesAtivos` já sai filtrado do container, o componente de
  // apresentação nunca vê um item de fase Baixa.
  //
  // `null` (distinto de `[]`) enquanto `fasesIds`/`antecedentes` ainda não
  // chegaram — fail-closed: nunca mostra a lista completa por engano antes
  // de confirmar quais itens são fase Baixa. `TableAntecedentesAtivos.jsx`
  // trata `null` como "carregando" e `[]` como "vazio de verdade", mesmo
  // padrão já usado em `ListaHistorico.jsx`/`historicos`.
  const antecedentesAtivos = useMemo(() => {
    if (fasesIds?.idFaseBaixa == null || !antecedentes) {
      return null;
    }
    const idFaseBaixaNum = Number(fasesIds.idFaseBaixa);
    return antecedentes.filter(a => Number(a.idFase) !== idFaseBaixaNum);
  }, [antecedentes, fasesIds]);

  // Bloco de Anotação (Etapa 5) — o serviço é chamado direto de dentro de
  // `BlocoAnotacao.jsx` (mesmo padrão de `DialogTitulo.jsx`); o container só
  // precisa saber recarregar a lista de Histórico após um "Anotar" bem
  // sucedido, reaproveitando `buscarHistoricos` já definido acima.
  // Decisão do usuário (confirmada nesta sessão): a grid "Antecedentes
  // Ativos" é só consulta — não existe seleção/replicação de Anotar/Enviar/
  // Agendar Compromisso em antecedentes (diferente do que o plano original
  // prescrevia); por isso não há `antecedentesSelecionadosHistorico` aqui.
  const onAnotarSucesso = useCallback(() => {
    const alvo = processo?.id;
    if (!alvo) {
      return;
    }
    buscarHistoricos(alvo, 0);
  }, [processo?.id, buscarHistoricos]);

  // Refresh pós-sucesso do Novo Acordo (AW2-1032 Etapa 7) — gerar um acordo
  // muda Saldo Principal/Subtotal/Total do processo, então os 2 cards que
  // dependem desses dados precisam ser recarregados sem exigir reload da
  // página. Chamado só pelos branches de SUCESSO de `resolverResultadoGeracao`
  // (`NovoAcordoPassoTres.jsx`) — nunca em erro, já que nenhuma mudança real
  // ocorreu no backend nesse caso.
  const onAcordoGerado = useCallback(() => {
    const alvo = processo?.id;
    if (!alvo) {
      return;
    }

    buscarAcompanhamentoDoProcessoService(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setDadosProcesso(data);
        }
      })
      .catch(() => {});

    getDadosResumo(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setInfoAdicionaisAcordo(data);
        }
      })
      .catch(() => {});
  }, [processo?.id]);

  // Refresh pós-sucesso da Etapa 8 (Mudança de Fase e Envio) —
  // `atualizarFaseProcessoService` muda `idFase`, então `sinalizacaoInfo`
  // (usado pelo próprio `MudancaFaseEnvio.jsx` para saber a fase atual do
  // processo) precisa ser recarregado, não só os cards de valor já
  // cobertos por `onAcordoGerado`. Também recarrega `historicos` — o campo
  // "Texto" deste formulário insere uma anotação no Histórico do processo
  // (mesmo hint da tela real: "A Anotação será inserida no Histórico do
  // Processo"), mesmo padrão já usado por `onAnotarSucesso`.
  const onFaseAlterada = useCallback(() => {
    const alvo = processo?.id;
    if (!alvo) {
      return;
    }

    findInformacoesAdicionaisInpescaoProcessoService(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setSinalizacaoInfo(data);
        }
      })
      .catch(() => {});

    buscarHistoricos(alvo, 0);
    onAcordoGerado();
  }, [processo?.id, buscarHistoricos, onAcordoGerado]);

  // Refresh pós-sucesso da Etapa 9 (Agendar/Excluir Compromisso) — o
  // agendamento não afeta `sinalizacaoInfo`/valores do processo (diferente
  // da Etapa 8), só o Histórico (mesmo hint da tela real: "O Compromisso
  // será inserido no Histórico do Processo") — reaproveita `buscarHistoricos`,
  // sem precisar de `onAcordoGerado`.
  const onAgendamentoAlterado = useCallback(() => {
    const alvo = processo?.id;
    if (!alvo) {
      return;
    }
    buscarHistoricos(alvo, 0);
  }, [processo?.id, buscarHistoricos]);

  // Refresh pós-sucesso de Unificar/Juntar (AW2-1033 Etapa 4) — a mutação
  // altera tanto a lista de antecedentes (o processo unificado/juntado some
  // da lista) quanto os cards que dependem de dado do processo principal
  // (mesma categoria de mutação que `onAcordoGerado`, reaproveitado aqui
  // sem duplicar seu guard). `buscarAntecedentes` já tem seu próprio guard
  // de requisição (`antecedentesIdProcessoRef`/`antecedentesRequestRef`) —
  // não precisa de um guard novo aqui, só orquestrar os 2 refreshes com os
  // mesmos defaults que a tela real usa após a mutação (`atualizarAntecedentesComStatusInicial`
  // sempre volta para página 0/ordenação padrão, nunca preserva a
  // página/ordenação que o usuário tinha antes de confirmar).
  const onAntecedenteMutado = useCallback(() => {
    const alvo = processo?.id;
    if (!alvo) {
      return;
    }

    setAntecedentesPagina(0);
    setAntecedentesTamanhoPagina(TAMANHO_PAGINA_ANTECEDENTES_PADRAO);
    setAntecedentesOrdenacao(ORDENACAO_ANTECEDENTES_PADRAO);
    setTipoConsultaAntecedentes(tiposConsultasAntecedentesTypes.simples);
    buscarAntecedentesComParametros(
      alvo,
      0,
      TAMANHO_PAGINA_ANTECEDENTES_PADRAO,
      ORDENACAO_ANTECEDENTES_PADRAO,
      tiposConsultasAntecedentesTypes.simples,
    );
    onAcordoGerado();
  }, [processo?.id, buscarAntecedentesComParametros, onAcordoGerado, setAntecedentesOrdenacao, setAntecedentesPagina, setAntecedentesTamanhoPagina]);

  // "Efetivo" para o gate de Unificar/Juntar (achado em code review) — a
  // tela real (`AntecedentesProcessos.jsx`) nunca permite Unificar/Juntar
  // quando o processo está na fase "Acordo Seguro Externo", mesmo que
  // `podeModificarFase` cru seja `true`. Escopo restrito a este gate: não
  // altera o `podeModificarFase` cru já usado por `BotaoNovoAcordo` (lacuna
  // pré-existente e fora do escopo desta etapa).
  //
  // Fail-closed enquanto `fasesIds`/`sinalizacaoInfo.idFase` ainda não
  // carregaram (achado na 2ª rodada de review — mesma classe de bug já
  // corrigida em `TableAntecedentesProcesso.jsx`/`ResumoAntecedenteAtual.jsx`,
  // repetida aqui pela própria correção deste derivado): sem os 2 valores,
  // não dá para confirmar que o processo NÃO está em Acordo Seguro Externo,
  // então o gate deve começar desabilitado (`false`) até a resposta chegar.
  const dadosDeFaseCarregados = Boolean(fasesIds) && sinalizacaoInfo?.idFase != null;
  const ehFaseAcordoSeguroExterno = dadosDeFaseCarregados
    && Number(sinalizacaoInfo?.idFase) === Number(fasesIds?.idFaseAcordoSeguroExterno);
  const podeModificarFaseUnificarJuntar = !dadosDeFaseCarregados || ehFaseAcordoSeguroExterno
    ? false
    : sinalizacaoInfo?.podeModificarFase;

  // Gate de Novo/Editar/Excluir Título (AW2-1034 Etapa 4) — mesma fórmula de
  // `cannotEnabled` da tela real (`Titulos.jsx`): bloqueia em qualquer uma
  // das 3 fases (Acordo Seguro Externo/Acordo Seguro/Baixa) OU cobrança
  // judicial. Reaproveita o mesmo guard `dadosDeFaseCarregados` acima — sem
  // fail-closed aqui, os 3 comparadores de fase (`Number(undefined) === Number(x)`
  // é sempre `false`) ficariam fail-open enquanto `fasesIds`/`sinalizacaoInfo`
  // ainda não carregaram, mesma classe de bug já documentada no CLAUDE.md do
  // módulo para esse padrão.
  const cannotEnabledTitulos = !dadosDeFaseCarregados
    || Number(sinalizacaoInfo?.idFase) === Number(fasesIds?.idFaseAcordoSeguroExterno)
    || Number(sinalizacaoInfo?.idFase) === Number(fasesIds?.idFaseAcordoSeguro)
    || Number(sinalizacaoInfo?.idFase) === Number(fasesIds?.idFaseBaixa)
    || sinalizacaoInfo?.sinalizacaoCoresQuery?.cobrancaJudicial === true;

  // Sucesso de Novo/Editar/Excluir Título — recompõe o "Cálculo do Processo"
  // (Valor Principal recomposto, AC explícito da Etapa 4) reaproveitando
  // `onAcordoGerado` (mesmo refresh já usado após Novo Acordo, AW2-1032
  // Etapa 7) e refaz o fetch da própria grid de Títulos na página/ordenação
  // atuais (não é um reset — diferente de `onAntecedenteMutado`, que sempre
  // volta para a página 0 após Unificar/Juntar; aqui a linha editada
  // continua na mesma página, então preservar a posição é o comportamento
  // esperado).
  const onTituloMutado = useCallback(() => {
    const alvo = processo?.id;
    if (!alvo) {
      return;
    }

    buscarTitulosComParametros(alvo, titulosPagina, titulosTamanhoPagina, titulosOrdenacao);
    buscarTodosTitulosBaixados(alvo);
    onAcordoGerado();
  }, [
    processo?.id, buscarTitulosComParametros, titulosPagina, titulosTamanhoPagina, titulosOrdenacao,
    buscarTodosTitulosBaixados, onAcordoGerado,
  ]);

  const renderAba = () => {
    if (!processo) {
      return (
        <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 6 }}>
          Selecione um processo na grade para ver os detalhes.
        </Typography>
      );
    }
    switch (aba) {
      case 'historico':
        return (
          <AbaHistorico
            processo={processo}
            limparCamposHistorico={limparCamposHistorico}
            deveResetarCamposHistorico={deveResetarCamposHistorico}
            onChangeLimparCamposHistorico={setLimparCamposHistorico}
            historicos={historicos}
            carregandoMaisHistoricos={carregandoMaisHistoricos}
            desabilitarFixarHistorico={desabilitarFixarHistorico}
            erroFixarHistorico={erroFixarHistorico}
            onScrollFimHistoricos={onScrollFimHistoricos}
            onFixarHistorico={onFixarHistorico}
            onFecharErroFixarHistorico={() => setErroFixarHistorico('')}
            antecedentesAtivos={antecedentesAtivos}
            tiposHistoricoOptions={tiposHistoricoOptions}
            onAnotarSucesso={onAnotarSucesso}
            sinalizacaoInfo={sinalizacaoInfo}
            onFaseAlterada={onFaseAlterada}
            tiposAgendamentosOptions={tiposAgendamentosOptions}
            tiposTitulosAgendamentosOptions={tiposTitulosAgendamentosOptions}
            onAgendamentoAlterado={onAgendamentoAlterado}
          />
        );
      case 'recebimento': return <AbaRecebimento processo={processo} />;
      case 'financeira': return <AbaFinanceira processo={processo} />;
      case 'documentos': return <AbaDocumentos />;
      case 'email': return <AbaEmail />;
      case 'devedor': return <AbaDevedor processo={processo} />;
      case 'cliente': return <AbaCliente processo={processo} />;
      case 'recalculos': return <AbaRecalculos />;
      case 'juridico': return <AbaJuridico />;
      case 'fases': return <AbaFases />;
      case 'acompanhamento':
      default: return (
        <AbaAcompanhamento
          processo={processo}
          dadosProcesso={dadosProcesso}
          antecedentes={antecedentes}
          antecedentesTotal={antecedentesTotal}
          antecedentesPagina={antecedentesPagina}
          antecedentesTamanhoPagina={antecedentesTamanhoPagina}
          antecedentesOrdenacao={antecedentesOrdenacao}
          antecedentesSelecionados={antecedentesSelecionados}
          tipoConsultaAntecedentes={tipoConsultaAntecedentes}
          onAlternarTipoConsultaAntecedentes={onAlternarTipoConsultaAntecedentes}
          onChangeOrdenacaoAntecedentes={onChangeOrdenacaoAntecedentes}
          onChangePaginaAntecedentes={onChangePaginaAntecedentes}
          onChangeTamanhoPaginaAntecedentes={onChangeTamanhoPaginaAntecedentes}
          onSelecionarAntecedente={onSelecionarAntecedente}
          onSelecionarTodosAntecedentes={onSelecionarTodosAntecedentes}
          fasesIds={fasesIds}
          idFaseProcesso={sinalizacaoInfo?.idFase}
          clientePermiteUnificar={sinalizacaoInfo?.permitirUnificarProcesso}
          podeModificarFaseUnificarJuntar={podeModificarFaseUnificarJuntar}
          podeModificarFase={sinalizacaoInfo?.podeModificarFase}
          processoComTitulosLiberados={infoAdicionaisAcordo?.processoComTitulosLiberados}
          cobrancaJudicial={sinalizacaoInfo?.sinalizacaoCoresQuery?.cobrancaJudicial}
          onAcordoGerado={onAcordoGerado}
          onAntecedenteMutado={onAntecedenteMutado}
          titulos={titulos}
          titulosTotal={titulosTotal}
          titulosPagina={titulosPagina}
          titulosTamanhoPagina={titulosTamanhoPagina}
          titulosOrdenacao={titulosOrdenacao}
          onChangeOrdenacaoTitulos={onChangeOrdenacaoTitulos}
          onChangePaginaTitulos={onChangePaginaTitulos}
          onChangeTamanhoPaginaTitulos={onChangeTamanhoPaginaTitulos}
          modoGridTitulos={modoGridTitulos}
          onAlternarModoGridTitulos={onAlternarModoGridTitulos}
          cannotEnabledTitulos={cannotEnabledTitulos}
          estadoDoDevedor={sinalizacaoInfo?.estadoDoDevedor}
          tipoTituloOptions={tipoTituloOptions}
          statusTituloOptions={statusTituloOptions}
          motivosBaixaOptions={motivosBaixaOptions}
          motivosExclusaoOptions={motivosExclusaoOptions}
          onTituloMutado={onTituloMutado}
          todosTitulosBaixados={todosTitulosBaixados}
        />
      );
    }
  };

  if (!resultado) {
    return (
      <AppShell titulo="Controle de processos">
        <CardWrapper sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {/*
              Texto varia por causa (achado em code review): sem essa
              distinção, uma busca automática por `nroProcesso` que falhou
              mostrava simultaneamente esta mensagem genérica de "nenhuma
              pesquisa ainda" E o Snackbar de erro logo abaixo — depois que o
              Snackbar some (4s), só sobrava o texto genérico, escondendo que
              uma busca de fato aconteceu e falhou.
            */}
            {erroBuscaPorNroProcesso ? RESULT_NOT_FOUND : 'Nenhuma pesquisa foi realizada ainda.'}
          </Typography>
          <ButtonUI onClick={() => history.push(rota('/pesquisa'))}>
            Ir para Pesquisa de Processos
          </ButtonUI>
        </CardWrapper>
        <Snackbar
          open={snackbarErroAberto}
          autoHideDuration={4000}
          onClose={() => setSnackbarErroAberto(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" variant="filled" onClose={() => setSnackbarErroAberto(false)}>
            {RESULT_NOT_FOUND}
          </Alert>
        </Snackbar>
      </AppShell>
    );
  }

  return (
    <AppShell titulo="Controle de processos" fullBleed>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'clamp(14px, 0.9vw, 26px)',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
        }}
      >
        <GridProcessos
          processos={processos}
          statusOptions={statusOptions}
          condicaoOptions={condicaoOptions}
          selecionado={processo}
          onSelecionar={setProcesso}
        />

        {/* minWidth: 0 é essencial — ver comentário no topo do arquivo. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <BarraAcoesProcesso
            idProcesso={processo?.id}
            sinalizacaoInfo={sinalizacaoInfo}
            setSinalizacaoInfo={setSinalizacaoInfo}
            requisicoesOptions={requisicoesSelector}
          />
          {renderAba()}
        </Box>

        <MenuLateralProcesso ativo={aba} onChange={setAba} />
      </Box>
    </AppShell>
  );
}
